/**
 * ลงทะเบียนพนักงานล่วงหน้า (trial onboarding) — owner วางรายชื่อ ชื่อ+สกุล+เลขบัตร
 * (ทีละบรรทัด คั่นด้วย , หรือ tab) → ลงทะเบียนรอ → ได้ลิงก์/QR ให้พนักงานเปิดในไลน์
 * เพื่อยืนยันตัวตน (เลขบัตร+ชื่อ) แล้วเชื่อมบัญชีเอง.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { UploadCloud, Copy, Check, QrCode } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRegisterEmployees, type RegisterResult } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { LIFF_ID } from '../../lib/liff';
import { useAuthStore } from '../../lib/store';

interface ParsedRow { first_name: string; last_name: string; national_id: string; ok: boolean; reason?: string }

function parse(text: string): ParsedRow[] {
  return text.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
    const parts = line.split(/[\t,]+/).map((p) => p.trim());
    const [first = '', last = '', rawNid = ''] = parts;
    const nid = rawNid.replace(/\D/g, '');
    let ok = true, reason: string | undefined;
    if (!first || !last) { ok = false; reason = 'ไม่มีชื่อ/สกุล'; }
    else if (nid.length !== 13) { ok = false; reason = 'เลขบัตรต้อง 13 หลัก'; }
    return { first_name: first, last_name: last, national_id: nid, ok, reason };
  });
}

export function OwnerRegisterEmployees() {
  const [text, setText] = useState('');
  const [result, setResult] = useState<RegisterResult | null>(null);
  const [copied, setCopied] = useState(false);
  const register = useRegisterEmployees();
  const toast = useToast();
  const profileCode = useAuthStore((s) => s.profile?.tenant_code ?? null);

  const rows = parse(text);
  const valid = rows.filter((r) => r.ok);
  const invalid = rows.filter((r) => !r.ok);
  const tenantCode = result?.tenant_code ?? profileCode ?? '';
  const claimLink = tenantCode && LIFF_ID ? `https://liff.line.me/${LIFF_ID}/claim?tenant=${tenantCode}` : '';

  function submit() {
    if (valid.length === 0) return toast.error('ไม่มีรายชื่อที่ถูกต้อง');
    register.mutate(valid.map(({ first_name, last_name, national_id }) => ({ first_name, last_name, national_id })), {
      onSuccess: (r) => { setResult(r); toast.success(`ลงทะเบียน ${r.registered} คน`); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ลงทะเบียนไม่สำเร็จ'),
    });
  }

  function copyLink() {
    navigator.clipboard?.writeText(claimLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  return (
    <AdminLayout title="ลงทะเบียนพนักงาน (นำเข้า)">
      <div className="max-w-2xl mx-auto space-y-4 pb-10">
        <p className="text-sm text-gray-500 px-1">
          วางรายชื่อพนักงาน ทีละบรรทัด — <b>ชื่อ, นามสกุล, เลขบัตรประชาชน</b> (คั่นด้วยจุลภาคหรือ Tab).
          ลงทะเบียนรอไว้ก่อน แล้วส่งลิงก์ให้พนักงานเปิดในไลน์เพื่อยืนยัน + เชื่อมบัญชีเอง (เงินเดือนตั้งทีหลัง).
        </p>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder={'สมชาย, ใจดี, 1234567890123\nสมหญิง, รักงาน, 1100700123456'}
          className="input font-mono text-sm w-full"
        />

        {rows.length > 0 && (
          <div className="text-sm flex gap-4">
            <span className="text-teal-700">พร้อมลงทะเบียน {valid.length}</span>
            {invalid.length > 0 && <span className="text-red-600">ข้าม {invalid.length} (ข้อมูลไม่ครบ/เลขบัตรผิด)</span>}
          </div>
        )}
        {invalid.length > 0 && (
          <div className="card p-3 text-xs text-gray-500 space-y-0.5">
            {invalid.slice(0, 8).map((r, i) => <div key={i}>• {r.first_name} {r.last_name} {r.national_id} — {r.reason}</div>)}
          </div>
        )}

        <button onClick={submit} disabled={register.isPending || valid.length === 0} className="btn-cta w-full justify-center flex items-center gap-2">
          <UploadCloud className="w-4 h-4" /> {register.isPending ? 'กำลังลงทะเบียน…' : `ลงทะเบียน ${valid.length} คน`}
        </button>

        {/* ผลลัพธ์ + ลิงก์/QR ให้พนักงาน */}
        {result && (
          <div className="card p-4 bg-teal-50/40 space-y-3">
            <p className="font-semibold text-gray-900">ลงทะเบียนแล้ว {result.registered} คน{result.skipped.length > 0 ? ` · ข้าม ${result.skipped.length}` : ''}</p>
            {/* fb#228 — บอกว่าข้ามใครเพราะอะไร (เช่น เลขบัตรซ้ำกับ EMP0066 … (ลาออก)) ไม่ใช่แค่จำนวน */}
            {result.skipped.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 space-y-0.5">
                {result.skipped.map((s, i) => <div key={i}>• {s.name} — {s.reason}</div>)}
              </div>
            )}
            {claimLink ? (
              <>
                <p className="text-sm text-gray-700">ส่งลิงก์นี้ให้พนักงาน (เปิดในไลน์ → กรอกเลขบัตร → ยืนยันชื่อ → เชื่อมบัญชี):</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border rounded-lg px-2 py-1.5 break-all">{claimLink}</code>
                  <button onClick={copyLink} className="btn-secondary shrink-0 inline-flex items-center gap-1">
                    {copied ? <Check className="w-4 h-4 text-teal" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500"><QrCode className="w-4 h-4" /> หรือสแกน QR:</div>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(claimLink)}`}
                  alt="QR ลงทะเบียน" width={160} height={160} className="rounded-lg border bg-white p-1 mx-auto"
                />
              </>
            ) : (
              <p className="text-sm text-amber-600">ยังสร้างลิงก์อัตโนมัติไม่ได้ (ไม่พบรหัสบริษัท) — ดูรหัสบริษัทได้ที่หน้าตั้งค่า แล้วส่งลิงก์ /claim?tenant=รหัส</p>
            )}
          </div>
        )}

        <div className="text-center"><Link to="/hr/people#all" className="text-sm text-teal hover:underline">ดูรายชื่อพนักงาน →</Link></div>
      </div>
    </AdminLayout>
  );
}
